import React, { useState, useEffect } from 'react';
import { ConsoleShell, useConsoleTheme } from '../components/Console';
import { Card, CardTitle, Insight } from '../components/UI';
import { supabase } from '../supabaseClient';
const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
function useIsMobile() {
  const [m,setM]=React.useState(()=>typeof window!=='undefined'&&window.innerWidth<768);
  React.useEffect(()=>{const h=()=>setM(window.innerWidth<768);window.addEventListener('resize',h);return()=>window.removeEventListener('resize',h);},[]);
  return m;
}
function TagNormalizerBody() {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const isMobile = useIsMobile();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [replace, setReplace] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const loadTags = async () => {
    setLoading(true);
    try {
    const { data } = await supabase.from('Locations').select('id,tags').not('tags','is',null).limit(5000);
    const map = {};
    (data||[]).forEach(r => {
      (r.tags||'').split(',').map(t=>t.trim()).filter(Boolean).forEach(t => {
        const key = t.toLowerCase();
        if (!map[key]) map[key]={raw:t,count:0,ids:[]};
        map[key].count++;
        map[key].ids.push(r.id);
      });
    });
    const sorted = Object.values(map).sort((a,b)=>b.count-a.count);
    setTags(sorted);
    } catch (_e) {} finally { setLoading(false); }
  };

  useEffect(() => { loadTags(); }, []);

  const applyReplace = async () => {
    if (!selected||!replace.trim()) return;
    setSaving(true);
    try {
      const { data: records } = await supabase.from('Locations').select('id,tags').in('id', selected.ids);
      if (!records || records.length === 0) {
        setMsg('No records found.');
        setSaving(false);
        return;
      }
      const updates = records.map(r => ({
        id: r.id,
        tags: (r.tags||'').split(',').map(t=>t.trim()).map(t=>t.toLowerCase()===selected.raw.toLowerCase()?replace.trim():t).filter(Boolean).join(', ')
      }));
      for (const update of updates) {
        await supabase.from('Locations').update({tags:update.tags}).eq('id',update.id);
      }
      setMsg(`✓ Replaced "${selected.raw}" with "${replace.trim()}" across ${updates.length} locations.`);
      setSelected(null);
      setReplace('');
      loadTags();
    } catch (_e) {
      setMsg('Error applying replacement.');
    } finally {
      setSaving(false);
    }
  };

  const S = {th:{fontSize:'.65rem',color:'var(--color-text-secondary)',textTransform:'uppercase',letterSpacing:'.04em',padding:'6px 8px',textAlign:'left',borderBottom:'1px solid var(--color-border-tertiary)',fontWeight:500},td:{fontSize:'.75rem',padding:'6px 8px',borderBottom:'.5px solid var(--color-border-tertiary)',color:'var(--color-text-primary)',cursor:'pointer'}};

  return (
    <div style={{fontFamily:F,padding:isMobile?'16px 14px 80px':'24px 28px'}}>
      <div style={{marginBottom:16}}>
        <h2 style={{fontSize:'1.1rem',fontWeight:500,margin:0,color:'var(--color-text-primary)'}}>Tag normalizer</h2>
        <p style={{fontSize:'.78rem',color:'var(--color-text-secondary)',margin:'4px 0 0'}}>View all tags by frequency. Click any tag to replace it with a normalized version across all locations.</p>
      </div>
      {msg&&<Insight type="ok" style={{marginBottom:12}}>{msg}</Insight>}
      <div style={{display:'grid',gridTemplateColumns:selected&&!isMobile?'1fr 320px':'1fr',gap:16,alignItems:'start'}}>
        <Card>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
            <CardTitle style={{marginBottom:0}}>{tags.length} unique tags</CardTitle>
            <button onClick={loadTags} disabled={loading} style={{fontSize:'.72rem',color:t.mut,background:'none',border:'1px solid var(--color-border-secondary)',borderRadius:6,padding:'3px 10px',cursor:'pointer',fontFamily:F}}>{loading?'Loading…':'↻ Refresh'}</button>
          </div>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',minWidth:280}}>
              <thead><tr><th style={S.th}>Tag</th><th style={S.th}>Count</th></tr></thead>
              <tbody>{tags.slice(0,100).map(t=>(
                <tr key={t.raw} onClick={()=>{setSelected(t);setReplace(t.raw);setMsg('');}} style={{background:selected?.raw===t.raw?'rgba(49,79,92,0.06)':''}}>
                  <td style={{...S.td,fontWeight:500}}>{t.raw}</td>
                  <td style={S.td}>{t.count}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </Card>
        {selected&&(
          <Card style={{position:isMobile?'static':'sticky',top:16}}>
            <CardTitle>Replace tag</CardTitle>
            <div style={{fontSize:'.72rem',color:'var(--color-text-secondary)',marginBottom:12}}>
              Replace <strong style={{color:'var(--color-text-primary)'}}>{selected.raw}</strong> ({selected.count} locations)
            </div>
            <input value={replace} onChange={e=>setReplace(e.target.value)} placeholder="New tag value…"
              style={{width:'100%',padding:'7px 10px',borderRadius:8,border:'1px solid var(--color-border-secondary)',background:'var(--color-background-secondary)',color:'var(--color-text-primary)',fontSize:'.78rem',fontFamily:F,boxSizing:'border-box',marginBottom:10}}/>
            <div style={{display:'flex',gap:8}}>
              <button onClick={applyReplace} disabled={saving||!replace.trim()} style={{flex:1,padding:'7px',borderRadius:8,background:t.accent,color:'#AFE1F6',border:'none',fontSize:'.72rem',fontWeight:500,cursor:'pointer',fontFamily:F}}>
                {saving?'Replacing…':'Replace all'}
              </button>
              <button onClick={()=>setSelected(null)} style={{padding:'7px 12px',borderRadius:8,background:'none',border:'1px solid var(--color-border-secondary)',color:'var(--color-text-secondary)',fontSize:'.72rem',cursor:'pointer',fontFamily:F}}>Cancel</button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

export default function TagNormalizer(props) {
  if (props && props.embed) return <TagNormalizerBody {...props} />;
  return <ConsoleShell title="Tag Normalizer"><TagNormalizerBody {...props} /></ConsoleShell>;
}
